import { supabase } from '../../../config/supabase';

export const ASSESSMENT_TEMP_CHART_BUCKET = 'assessment-temp-images';

export type TemporaryChartStageEntry = {
  path: string;
  signedUrl: string;
};

export type TemporaryChartStageMap = Record<string, TemporaryChartStageEntry>;

const inferExtensionFromBlob = (blob: Blob): string => {
  switch (blob.type) {
    case 'image/jpeg':
      return 'jpg';
    case 'image/webp':
      return 'webp';
    default:
      return 'png';
  }
};

const dataUrlToBlob = async (dataUrl: string): Promise<Blob> => {
  const response = await fetch(dataUrl);
  if (!response.ok) {
    throw new Error(`Falha ao converter chart temporário: ${response.status}`);
  }
  return response.blob();
};

const buildTempChartPath = (clientId: string, scope: string, objectId: string, key: string, extension: string) => {
  const timestamp = Date.now();
  const randomSuffix = Math.random().toString(36).slice(2, 8);
  return `${clientId}/temporary-charts/${scope}/${objectId}/${timestamp}-${randomSuffix}-${key}.${extension}`;
};

export class AssessmentTempChartStorageService {
  static async stageDataUrlMap(
    clientId: string,
    scope: string,
    objectId: string,
    images: Record<string, string | null | undefined>,
    options?: { signedUrlExpiresInSeconds?: number },
  ): Promise<TemporaryChartStageMap> {
    const expiresIn = options?.signedUrlExpiresInSeconds ?? 120;
    const stagedEntries = await Promise.all(
      Object.entries(images).map(async ([key, dataUrl]) => {
        if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image/')) {
          return null;
        }

        const blob = await dataUrlToBlob(dataUrl);
        const extension = inferExtensionFromBlob(blob);
        const path = buildTempChartPath(clientId, scope, objectId, key, extension);

        const { error: uploadError } = await supabase.storage
          .from(ASSESSMENT_TEMP_CHART_BUCKET)
          .upload(path, blob, {
            cacheControl: '60',
            upsert: true,
            contentType: blob.type || 'image/png',
          });

        if (uploadError) {
          throw uploadError;
        }

        const { data: signedUrlData, error: signedUrlError } = await supabase.storage
          .from(ASSESSMENT_TEMP_CHART_BUCKET)
          .createSignedUrl(path, expiresIn);

        if (signedUrlError || !signedUrlData?.signedUrl) {
          throw signedUrlError ?? new Error('Não foi possível criar URL assinada para o chart temporário.');
        }

        return [key, { path, signedUrl: signedUrlData.signedUrl }] as const;
      }),
    );

    return stagedEntries.reduce<TemporaryChartStageMap>((accumulator, entry) => {
      if (!entry) return accumulator;
      accumulator[entry[0]] = entry[1];
      return accumulator;
    }, {});
  }

  static async cleanup(pathsOrEntries: string[] | TemporaryChartStageMap | null | undefined): Promise<void> {
    const paths = Array.isArray(pathsOrEntries)
      ? pathsOrEntries
      : pathsOrEntries
        ? Object.values(pathsOrEntries).map((entry) => entry.path)
        : [];

    if (!paths.length) return;

    const { error } = await supabase.storage.from(ASSESSMENT_TEMP_CHART_BUCKET).remove(paths);
    if (error) {
      console.warn('Erro ao limpar charts temporários no Supabase Storage:', error);
    }
  }
}
